// ./src/components/generic/PlayListSelector/PlayListSelector.tsx
import React, { useMemo, useEffect } from "react";
import { Select, MultiSelect, MultiSelectProps } from "@mantine/core";
import { usePlayLists } from "@/hooks/usePlayLists";
import { LoadingIndicator } from "@/components/generic";
import { PlayList } from "@/utils/api";
import { useApp } from "@/contexts";

export interface PlayListSelectorProps {
  /** Selected playlist ID(s) - string for single select, string[] for multiple */
  value: string | string[] | null | undefined;

  /** Callback when playlist selection changes */
  onChange?: (
    playListId: string | string[] | null,
    playList?: PlayList | PlayList[]
  ) => void;

  /** Whether to allow multiple selection */
  multiple?: boolean;

  /** Placeholder text */
  placeholder?: string;

  /** Whether to include inactive playlists */
  includeInactive?: boolean;

  /** Custom filter function for playlists */
  playListFilter?: (playList: PlayList) => boolean;

  /** Whether the field is required */
  required?: boolean;

  /** Loading placeholder text */
  loadingPlaceholder?: string;

  /** Empty state text */
  emptyText?: string;

  /** Error message */
  error?: React.ReactNode;

  /** Whether the field is disabled */
  disabled?: boolean;

  /** Component size */
  size?: "xs" | "sm" | "md" | "lg" | "xl";

  /** Additional props passed to Select/MultiSelect */
  [key: string]: any;
}

export const PlayListSelector: React.FC<PlayListSelectorProps> = ({
  value,
  onChange,
  multiple = false,
  placeholder = "Select a playlist",
  includeInactive = false,
  playListFilter,
  required = false,
  loadingPlaceholder = "Loading playlists...",
  emptyText = "No playlists available",
  error,
  disabled,
  size = "md",
  ...selectProps
}) => {
  // Use playlists hook
  const { isAuthenticated } = useApp();
  const { playLists, loading, isEmpty, lastError } = usePlayLists({
    autoLoad: true,
    isAuthenticated,
  });

  // Filter and prepare playlist options
  const playListOptions = useMemo(() => {
    let filteredPlayLists = playLists;

    // Apply status filter (status 100 = active, 0 = disabled)
    if (!includeInactive) {
      filteredPlayLists = filteredPlayLists.filter(
        (playList) => playList.status === 100 // Only show active playlists
      );
    }

    // Apply custom filter if provided
    if (playListFilter) {
      filteredPlayLists = filteredPlayLists.filter(playListFilter);
    }

    // Sort by name for better UX
    filteredPlayLists.sort((a, b) => {
      const nameA = a.name || "Unnamed Playlist";
      const nameB = b.name || "Unnamed Playlist";
      return nameA.localeCompare(nameB);
    });

    // Convert to select options
    return filteredPlayLists.map((playList) => ({
      value: playList.id || "",
      label: playList.name || "Unnamed Playlist",
      disabled: playList.status === 0, // Disabled playlists have status 0
    }));
  }, [playLists, includeInactive, playListFilter]);

  // Create a map for quick playlist lookup by ID
  const playListMap = useMemo(() => {
    const map = new Map();
    playLists.forEach((playList) => {
      if (playList.id) {
        map.set(playList.id, playList);
      }
    });
    return map;
  }, [playLists]);

  // Auto-select first playlist if required and no value provided
  useEffect(() => {
    if (required && playListOptions.length > 0 && !loading && onChange) {
      // For single select mode
      if (!multiple && !value) {
        const firstPlayList = playListOptions[0];
        if (firstPlayList && !firstPlayList.disabled) {
          const playListObject = playListMap.get(firstPlayList.value);
          onChange(firstPlayList.value, playListObject);
        }
      }
      // For multiple select mode
      else if (
        multiple &&
        (!value || (Array.isArray(value) && value.length === 0))
      ) {
        const firstPlayList = playListOptions[0];
        if (firstPlayList && !firstPlayList.disabled) {
          const playListObject = playListMap.get(firstPlayList.value);
          onChange([firstPlayList.value], [playListObject]);
        }
      }
    }
  }, [
    required,
    value,
    playListOptions,
    loading,
    onChange,
    playListMap,
    multiple,
  ]);

  // Handle onChange with playlist object lookup
  const handleChange = (selectedValue: string | string[] | null) => {
    if (!multiple) {
      // Single select mode
      const selectedPlayListId = selectedValue as string | null;
      if (selectedPlayListId) {
        const selectedPlayList = playListMap.get(selectedPlayListId);
        onChange?.(selectedPlayListId, selectedPlayList);
      } else {
        // When clearing selection, pass null for both ID and playlist
        onChange?.(null, undefined);
      }
    } else {
      // Multiple select mode
      const selectedPlayListIds = selectedValue as string[] | null;
      if (selectedPlayListIds && selectedPlayListIds.length > 0) {
        const selectedPlayLists = selectedPlayListIds
          .map((id) => playListMap.get(id))
          .filter(Boolean); // Remove any undefined values
        onChange?.(selectedPlayListIds, selectedPlayLists);
      } else {
        // When clearing selection, pass empty arrays
        onChange?.([], []);
      }
    }
  };

  // Determine the current state and configure select props accordingly
  const getSelectState = () => {
    // Loading state
    if (loading && playLists.length === 0) {
      return {
        data: [],
        placeholder: loadingPlaceholder,
        disabled: true,
        rightSection: <LoadingIndicator size={size} color="gray" />,
      };
    }

    // Error state
    if (lastError && !loading) {
      return {
        data: [],
        placeholder: "Error loading playlists",
        disabled: true,
        error: error || "Failed to load playlists",
      };
    }

    // Empty state
    if (isEmpty && !loading) {
      return {
        data: [],
        placeholder: emptyText,
        disabled: true,
      };
    }

    // Normal state with data
    return {
      data: playListOptions,
      placeholder,
      disabled: disabled || loading,
      rightSection:
        loading && playLists.length > 0 ? (
          <LoadingIndicator size={size} color="gray" />
        ) : (
          selectProps.rightSection
        ),
    };
  };

  const selectState = getSelectState();

  // Normalize value for the select component
  const normalizedValue = multiple
    ? Array.isArray(value)
      ? value
      : []
    : value ?? null;

  const renderMultiSelectOption: MultiSelectProps["renderOption"] = ({
    option,
  }) => {
    return option.label;
  };

  // Render the appropriate select component
  if (multiple) {
    return (
      <MultiSelect
        {...selectProps}
        size={size}
        data={selectState.data}
        renderOption={renderMultiSelectOption}
        value={normalizedValue as string[]}
        onChange={handleChange}
        placeholder={selectState.placeholder}
        clearable={!required}
        searchable
        required={required}
        disabled={selectState.disabled}
        error={selectState.error || error}
        rightSection={selectState.rightSection}
      />
    );
  }

  // Render single select
  return (
    <Select
      {...selectProps}
      size={size}
      data={selectState.data}
      value={normalizedValue as string | null}
      onChange={handleChange}
      placeholder={selectState.placeholder}
      clearable={!required}
      searchable
      required={required}
      disabled={selectState.disabled}
      error={selectState.error || error}
      rightSection={selectState.rightSection}
    />
  );
};
